import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const { loginWithCode } = useAuth();
  const navigate = useNavigate();

  const [codeEmail, setCodeEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState('');

  const [loginCode, setLoginCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [codeError, setCodeError] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setRequestError('');
    setRequestLoading(true);
    try {
      await authApi.requestLoginCode(codeEmail);
      setCodeSent(true);
    } catch (err) {
      setRequestError(err.response?.data?.message || 'Failed to send code. Please try again.');
    }
    setRequestLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setCodeError('');
    setVerifyLoading(true);
    const result = await loginWithCode(codeEmail, loginCode);
    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setCodeError(result.error);
    }
    setVerifyLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl shadow-lg p-12 max-w-md w-full text-center">
        <Logo size={64} className="mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-3">UtilitEase</h1>
      

        {/* Email code login */}
        {!codeSent ? (
          <form onSubmit={handleRequestCode} className="text-left space-y-3">
            {requestError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {requestError}
              </div>
            )}
            <input
              type="email"
              required
              placeholder="Enter your email"
              value={codeEmail}
              onChange={(e) => setCodeEmail(e.target.value)}
              disabled={requestLoading}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              disabled={requestLoading}
              className="w-full px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-60 transition"
            >
              {requestLoading ? 'Sending...' : 'Send sign-in code'}
            </button>
          </form>
        ) : (
          <div className="text-left space-y-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              <p className="font-medium">Check your email</p>
              <p className="text-green-700 mt-0.5">We sent an 8-digit code to <strong>{codeEmail}</strong>. It expires in 15 minutes.</p>
            </div>
            <form onSubmit={handleVerifyCode} className="space-y-3">
              {codeError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {codeError}
                </div>
              )}
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={8}
                placeholder="Enter 8-digit code"
                value={loginCode}
                onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                disabled={verifyLoading}
                autoFocus
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-center font-mono tracking-widest text-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="submit"
                disabled={verifyLoading || loginCode.length !== 8}
                className="w-full px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-60 transition"
              >
                {verifyLoading ? 'Verifying...' : 'Sign in'}
              </button>
            </form>
            <button
              type="button"
              onClick={() => { setCodeSent(false); setLoginCode(''); setCodeError(''); setCodeEmail(''); }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium w-full text-center"
            >
              Send another code
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
